import {EstimateWithComponentsDto} from "../../shared/dtos/estimate-with-components.dto";
import {LatLngDto, UpdateEstimateDragPathDto} from "../../shared/dtos/estimate-drag-path.dto";

// Only pure functions should be in this file, this also makes these function safe to call in listener callbacks where state updates don't exist

const METERS_IN_FOOT = 0.3048;

/**
 * The real-world truck + chipper: 45ft long, cab to the back of the chipper. The icon is
 * drawn at exactly this footprint — no display scaling — so an estimator can judge whether
 * the machine actually fits a driveway or the gap between two houses. Both the icon size and
 * the rear-center dragline anchor derive from this; the Android map uses the same 45ft
 * (MapGeometry.CHIPPER_LENGTH_FEET), so the two clients persist identical rear anchors and
 * agree on every route's distanceFeet. Keep them in sync.
 */
export const CHIPPER_LENGTH_FEET = 45;
/** Length the icon is drawn at, in feet — true scale, see above. */
const CHIPPER_DRAWN_LENGTH_FEET = CHIPPER_LENGTH_FEET;
// Width relative to that length, matching the proportions of truck_chipper_icon.png
// (80x289px, drawn front-up so 0 degrees of heading points north). Keep in sync with the asset.
export const CHIPPER_ICON_WIDTH_TO_LENGTH_RATIO = 80 / 289;
// Icons never shrink below this fraction of their full-zoom size, so a zoomed-out
// truck & chipper is still visible even though it's no longer proportional to the map.
export const CHIPPER_MIN_SCALE_FRACTION = 0.35;

const EARTH_CIRCUMFERENCE_METERS = 40075016.686;

/** Meters represented by one screen pixel in the Web Mercator projection at a given zoom/latitude. */
const metersPerPixelAtZoom = (lat: number, zoom: number) =>
  (EARTH_CIRCUMFERENCE_METERS * Math.cos(lat * Math.PI / 180)) / Math.pow(2, zoom + 8);

/**
 * Pixel length the truck & chipper icon is drawn at when the map is at `referenceZoomLevel`
 * (the zoom level other map icons treat as "full scale"), scaled off its real-world length.
 */
export const getChipperIconLengthPixels = (lat: number, referenceZoomLevel: number) => {
  const lengthMeters = CHIPPER_DRAWN_LENGTH_FEET * METERS_IN_FOOT;
  return lengthMeters / metersPerPixelAtZoom(lat, referenceZoomLevel);
};

/** Icon scale for a chipper: shrinks with zoom like other icons, but never below the visibility floor. */
export const getChipperIconScale = (mapScale: number) => Math.max(mapScale, CHIPPER_MIN_SCALE_FRACTION);

/**
 * The rear-center of the chipper attachment — the branch-chipping location. Draglines should
 * terminate here (not the truck's raw lat/lng) so they visually connect to where material is
 * actually fed in, and stay correct as the truck & chipper is rotated. Measured against the
 * drawn length so the endpoint lands on the icon's rear edge.
 */
export const getChipperRearCenterAnchor = (chipper: {lat: number, lng: number, heading: number}): LatLngDto => {
  const halfLengthMeters = (CHIPPER_DRAWN_LENGTH_FEET * METERS_IN_FOOT) / 2;
  const rearHeadingDegrees = (chipper.heading + 180) % 360;
  const offsetPoint = google.maps.geometry.spherical.computeOffset(
    new google.maps.LatLng(chipper.lat, chipper.lng),
    halfLengthMeters,
    rearHeadingDegrees
  );
  return {lat: offsetPoint.lat(), lng: offsetPoint.lng()};
};

export const getPathDistanceFeet = (pathPoints: LatLngDto[]) => {
  let totalDistance = 0;
  for(let i = 0; i < pathPoints.length - 1; i++) {
    const distance = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(pathPoints[i].lat, pathPoints[i].lng), new google.maps.LatLng(pathPoints[i + 1].lat, pathPoints[i + 1].lng));
    totalDistance += (distance / METERS_IN_FOOT);
  }
  return totalDistance;
}

export const findNearestChipperTruckWithDistance = (estimate: EstimateWithComponentsDto, lat: number, lng: number, excludeChipperId?: number) => {
  return estimate?.additionalComponents
    .filter(ac => ac.type === 'chipper' && ac.id !== excludeChipperId)
    .map(chipperTruck => ({
      chipperTruck,
      distance: google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(chipperTruck.lat, chipperTruck.lng), new google.maps.LatLng(lat, lng))
    }))
    .sort((a, b) => a.distance - b.distance)[0];
}

export const getDragPathUpdate = (estimate: EstimateWithComponentsDto, path: google.maps.MVCArray<google.maps.LatLng>, pathId: number, changedPointIndex: number): UpdateEstimateDragPathDto => {
  const points = path.getArray().map(p => ({lat: p.lat(), lng: p.lng()}));
  const changedPoint = points[changedPointIndex];
  const dragPathDto = estimate.dragPaths.find(d => d.id === pathId);
  const nearestDifferentChipperTruck = findNearestChipperTruckWithDistance(estimate, changedPoint.lat, changedPoint.lng, dragPathDto?.chipperTruckId);

  if(nearestDifferentChipperTruck && nearestDifferentChipperTruck.distance < 5) {
    const pointsToKeep = points.slice(0, changedPointIndex);
    const rearCenterAnchor = getChipperRearCenterAnchor(nearestDifferentChipperTruck.chipperTruck);
    const newPoints = [...pointsToKeep, rearCenterAnchor]
    const distanceFeet = getPathDistanceFeet(newPoints);
    return {
      points: newPoints,
      distanceFeet,
      chipperTruckId: nearestDifferentChipperTruck.chipperTruck.id
    }
  } else {
    return { points, distanceFeet: getPathDistanceFeet(points) }
  }
}

